import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';

import { Subscription, race } from 'rxjs';
import { map, take, startWith } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AdaptiveService } from '@uxdf/direct-shared-lib';

import { SegmentsConfigService } from '../../../../redux/services/invoices/segments.config.service';
import { SegmentsService } from '../../../../redux/services/invoices/segments.service';
import { Options } from '../../../../redux/models/invoices/segments.options.model';
import { ProviderOption } from '../../../../redux/models/invoices/segments.report.model';
import { State as ConfigState } from '../../../../redux/reducers/invoices/reducers-segments-config';
import { SegmentsState } from '../../../../redux/reducers';
import { Config } from '../../../../redux/models/invoices/segments.config.model';
import { SegmentsFiltersService, IOption } from './segments-filters.service';

@Component({
  standalone: false,
  selector: 'app-invoices-segments-filters',
  templateUrl: './segments-filters.component.html',
  animations: [
    trigger('toggle', [
      transition(':enter', [style({ height: 0, overflow: 'hidden' }), animate(300, style({ height: '*' }))]),
      transition(':leave', [style({ height: '*', overflow: 'hidden' }), animate(300, style({ height: 0 }))])
    ])
  ],
  providers: [SegmentsFiltersService]
})
export class SegmentsFiltersComponent implements OnInit, OnDestroy {
  @HostBinding() class = 'segments-filters';
  @ViewChild('filtersModal', { static: true }) private readonly filtersTemplate: ElementRef;
  // localized strings
  readonly multiselectValueShortened = $localize`:@@segments-filters.multiselectValueShortened:+{0} others...`;
  readonly accountInvoicesFilterLabelDateFrom = $localize`:@@segments-filters.accountInvoicesFilterLabelDateFrom:From`;
  readonly accountInvoicesFilterLabelDateTo = $localize`:@@segments-filters.accountInvoicesFilterLabelDateTo:To`;

  formGroup: UntypedFormGroup;
  opened = false;
  officeCodes: IOption[];
  officesLength = 0;
  segmentOptions: IOption[];
  providerOptions: IOption[];
  badgeLabels: string[];
  isMobile: boolean;
  private lastApplied: Options;
  private readonly subscriptions: Subscription;

  constructor(
    private readonly componentService: SegmentsFiltersService,
    private readonly configService: SegmentsConfigService,
    private readonly segmentsService: SegmentsService,
    private readonly modalService: NgbModal,
    private readonly adaptiveService: AdaptiveService
  ) {
    this.segmentOptions = [
      { value: 'All', label: 'All' },
      { value: 'Air', label: 'Air' },
      { value: 'Car', label: 'Car' },
      { value: 'Hotel', label: 'Hotel' },
      { value: 'Cruise', label: 'Cruise' },
      { value: 'Rail', label: 'Rail' }
    ];
    this.providerOptions = [{ value: 'All', label: 'All' }];
    this.subscriptions = new Subscription();
  }

  get dateFromControl(): AbstractControl {
    return this.formGroup.get('dateFrom');
  }
  get dateToControl() {
    return this.formGroup.get('dateTo');
  }

  getDateErrorMessage(control: AbstractControl) {
    return this.componentService.getDateErrorMessage(control);
  }

  ngOnInit() {
    this.formGroup = this.componentService.createFormGroup();
    this.setStateHandler();
    this.setInitialState();
    this.initSubscriptions();
    this.initDatesValidation();
  }

  toggleFilters() {
    this.opened = !this.opened;
    if (this.opened && this.isMobile) {
      this.modalService.open(this.filtersTemplate).result.then(
        () => {
          // apply
          this.componentService.apply();
          this.opened = false;
        },
        () => {
          // cancel
          this.formGroup.patchValue(this.lastApplied, { emitEvent: false });
          const options = this.componentService.formValueChangeHandler(this.lastApplied);
          this.componentService.reset(options);
          this.opened = false;
        }
      );
    }
  }

  // handlers for office multi select with "All" option
  groupByFn = item => this.officeCodes.indexOf(item) !== -1;
  isAll = items => items.children.filter(item => item.selected).length === this.officesLength;

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setStateHandler() {
    this.componentService.registerStateHandler((formValue: Options, apply: boolean) => {
      if (apply) {
        this.lastApplied = formValue;
      }
      this.formGroup.patchValue(formValue, { emitEvent: false });
      this.updateBadgeLabels(formValue);
    });
  }

  private updateBadgeLabels(formValue: any) {
    const providerOption = this.providerOptions.find(({ value }) => value === formValue.provider);
    this.badgeLabels = this.componentService.formValueToBadgeLabels(
      {
        ...formValue,
        provider: providerOption ? providerOption.label : null
      },
      this.officesLength
    );
  }

  private initDatesValidation() {
    this.dateFromControl.setValidators([control => this.componentService.validateDateLength(control)]);
    this.dateToControl.setValidators([control => this.componentService.validateDateLength(control)]);
  }

  private setMinDateFrom(): boolean {
    const fromValue = this.dateFromControl.value;
    const toValue = this.dateToControl.value;
    if (!fromValue || !fromValue.month || !fromValue.year) {
      return false;
    }
    if (!toValue || !toValue.month || !toValue.year) {
      return false;
    }
    if (fromValue.year > toValue.year || (fromValue.year === toValue.year && fromValue.month > toValue.month)) {
      // without async wrapper it renders previous value
      requestAnimationFrame(() => this.dateToControl.setValue({ ...fromValue }));
      return true;
    }

    return false;
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(formValue => {
        if (!this.formGroup.valid || this.setMinDateFrom()) {
          return;
        }
        const options = this.componentService.formValueChangeHandler(formValue);
        this.componentService.update(options, !(this.opened && this.isMobile));
      })
    );
    this.subscriptions.add(
      this.adaptiveService
        .getResizeEvent()
        .pipe(
          startWith(this.adaptiveService.isMobile()),
          map(() => this.adaptiveService.isMobile())
        )
        .subscribe(isMobile => {
          this.isMobile = isMobile;
          this.opened = false;
        })
    );
    this.subscriptions.add(
      this.segmentsService
        .getSegments()
        .pipe(map(segmentsState => segmentsState.report.data.providerList))
        .subscribe(this.updateFromReportData.bind(this))
    );
  }

  private updateFromReportData(providerList: ProviderOption[]) {
    const newProviderOptions = [{ value: 'All', label: 'All' }];
    if (providerList) {
      newProviderOptions.push(
        ...providerList.map(option => ({
          value: option.code,
          label: option.name
        }))
      );
    }
    const providerCodes = newProviderOptions.map(({ value }) => value);
    this.providerOptions = newProviderOptions;
    const providerValue = this.formGroup.value.provider;
    let newValue = providerValue;
    const isDisabled =
      ['All', 'Cruise', 'Rail'].indexOf(this.formGroup.value.segment) !== -1 || newProviderOptions.length <= 2;
    if (providerCodes.indexOf(providerValue) === -1 || isDisabled) {
      newValue = providerCodes.length === 2 ? providerCodes[1] : 'All';
    }
    this.formGroup.get('provider').reset({ value: newValue, disabled: isDisabled }, { emitEvent: false });
  }

  private updateFromConfig(config: Config) {
    const newValue: any = {
      offices: config.offices.map(({ name }) => name)
    };
    if (config.latestMonth) {
      const dateParts = config.latestMonth.split('-');
      newValue.dateFrom = {
        year: +dateParts[0],
        month: +(dateParts[1] || 1)
      };
      newValue.dateTo = { ...newValue.dateFrom };
      this.dateFromControl.setValidators([
        this.componentService.createDateFromValidator(newValue.dateFrom),
        control => this.componentService.validateDateLength(control)
      ]);
      this.dateFromControl.updateValueAndValidity();
    }
    this.formGroup.patchValue(newValue, { emitEvent: true });
  }

  private updateOfficeCodes(config: Config) {
    this.officeCodes = config.offices.map(office => ({
      value: office.name,
      label: office.name + ', ' + office.description
    }));
    this.officesLength = config.offices.length;
  }

  private setInitialState() {
    race([this.segmentsService.getNewLoadedSegments(), this.configService.getConfig()])
      .pipe(take(1))
      .subscribe((state: SegmentsState | ConfigState) => {
        // combined state has been loaded already
        if ('options' in state) {
          const options = state.options.data;
          const formValue = this.componentService.convertStateToFormValue(options);
          this.formGroup.patchValue(formValue, { emitEvent: false });
          this.updateOfficeCodes(options);
          this.updateBadgeLabels(formValue);
          return;
        }
        // first time initialization from config
        this.updateOfficeCodes(state.data);
        setTimeout(() => this.updateFromConfig(state.data));
      });
  }
}
